import { useQueryClient } from '@tanstack/react-query'
import { useSubscription } from '@trpc/tanstack-react-query'
import { useCallback, useEffect, useSyncExternalStore } from 'react'

import { useAuth } from '@/lib/auth'
import { signalAccountSessionRevalidation } from '@/lib/account-transition-signal'
import { canHoldStream } from '@/lib/realtime-transport'
import { useTRPC } from '@/lib/trpc'
import { isSocialQuery } from '@/social/social-provider'

let userStreamHealthy = false
const healthListeners = new Set<() => void>()

function setUserStreamHealthy(next: boolean): void {
  if (userStreamHealthy === next)
    return
  userStreamHealthy = next
  for (const listener of healthListeners)
    listener()
}

function subscribeToHealth(onChange: () => void): () => void {
  healthListeners.add(onChange)
  return () => {
    healthListeners.delete(onChange)
  }
}

export function useUserStreamHealthy(): boolean {
  return useSyncExternalStore(
    subscribeToHealth,
    () => userStreamHealthy,
    () => false,
  )
}

export function useUserStream(): void {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { actor, authResolved } = useAuth()

  const invalidateSocial = useCallback(() => {
    void queryClient.invalidateQueries({ predicate: isSocialQuery })
  }, [queryClient])

  const invalidateGameSummaries = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: trpc.userRouter.getCheckInHistory.queryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: trpc.userRouter.gameAttention.queryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: trpc.userRouter.getUserGames.queryKey(),
    })
  }, [queryClient, trpc])

  const invalidateReview = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: trpc.reviewRouter.getMessages.queryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: trpc.reviewRouter.listSessions.queryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: trpc.reviewRouter.getUsageSummary.queryKey(),
    })
  }, [queryClient, trpc])

  const invalidateGamePasses = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: trpc.promoGamePassRouter.summary.queryKey() })
    void queryClient.invalidateQueries({ queryKey: trpc.userRouter.getGameRestrictions.queryKey() })
  }, [queryClient, trpc])

  const invalidateFor = useCallback(
    (frame: { type: string }) => {
      if (frame.type === 'account.game-passes-changed') {
        invalidateGamePasses()
        return
      }
      if (frame.type === 'account.session-changed') {
        signalAccountSessionRevalidation()
        return
      }
      if (frame.type === 'game.turn-ready' || frame.type === 'game.summary') {
        invalidateGameSummaries()
        return
      }
      if (frame.type === 'review.changed') {
        invalidateReview()
        return
      }
      if (frame.type === 'stream.resync') {
        invalidateGamePasses()
        invalidateSocial()
        invalidateGameSummaries()
        invalidateReview()
        return
      }
      invalidateSocial()
    },
    [invalidateGamePasses, invalidateGameSummaries, invalidateReview, invalidateSocial],
  )

  const invalidateEverything = useCallback(() => {
    invalidateGamePasses()
    invalidateSocial()
    invalidateGameSummaries()
    invalidateReview()
  }, [invalidateGamePasses, invalidateGameSummaries, invalidateReview, invalidateSocial])

  const subscription = useSubscription(
    trpc.userEventsRouter.onUserChanged.subscriptionOptions(undefined, {
      enabled: canHoldStream(actor, authResolved),
      onData: invalidateFor,
      onConnectionStateChange: (state) => {
        if (state.state === 'pending')
          invalidateEverything()
      },
      onError: () => {},
    }),
  )

  useEffect(() => {
    setUserStreamHealthy(subscription.status === 'pending')
    return () => setUserStreamHealthy(false)
  }, [subscription.status])
}

export function UserStream(): null {
  useUserStream()
  return null
}
